import { useMemo, useState } from 'react';
import {
  Autocomplete, Avatar, Box, Stack, Typography, TextField, Button, IconButton,
  Paper, alpha, Grid, Chip, InputAdornment,
} from '@mui/material';
import { Add, Remove, RestaurantMenu, Search } from '@mui/icons-material';
import { formatMoney } from '../../utils/currency';
import { resolveAssetUrl } from '../../utils/assetUrl';

export type DispatchRestaurant = {
  _id: string;
  name: string;
  logo?: string;
  coverImage?: string;
  cuisineTypes?: string[];
  rating?: number;
  isOpen?: boolean;
  minOrderAmount?: number;
  avgPreparationTime?: number;
  phone?: string;
};

export type FoodMenuItem = {
  _id: string;
  name: string;
  price?: number;
  discountedPrice?: number;
  image?: string;
  isVeg?: boolean;
  isAvailable?: boolean;
  description?: string;
  categoryName?: string;
};

export type FoodCartLine = { menuItemId: string; quantity: number };

type Props = {
  accent: string;
  restaurants: DispatchRestaurant[];
  restaurantId: string;
  onRestaurantChange: (id: string) => void;
  menu: FoodMenuItem[];
  cart: FoodCartLine[];
  onCartChange: (cart: FoodCartLine[]) => void;
  fieldErrors?: Record<string, string>;
};

function unitPrice(item: FoodMenuItem): number {
  if (item.discountedPrice != null && item.discountedPrice > 0) return item.discountedPrice;
  return Number(item.price || 0);
}

export default function DispatchFoodMenuPicker({
  accent, restaurants, restaurantId, onRestaurantChange, menu, cart, onCartChange, fieldErrors = {},
}: Props) {
  const [menuQuery, setMenuQuery] = useState('');
  const [vegFilter, setVegFilter] = useState<'all' | 'veg' | 'nonveg'>('all');

  const selected = useMemo(
    () => restaurants.find((r) => r._id === restaurantId) || null,
    [restaurants, restaurantId],
  );

  const qtyById = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of cart) map.set(line.menuItemId, line.quantity);
    return map;
  }, [cart]);

  const cartItems = useMemo(() => {
    return cart
      .map((line) => {
        const item = menu.find((m) => m._id === line.menuItemId);
        if (!item) return null;
        return { ...line, item, lineTotal: unitPrice(item) * line.quantity };
      })
      .filter(Boolean) as Array<FoodCartLine & { item: FoodMenuItem; lineTotal: number }>;
  }, [cart, menu]);

  const cartTotal = cartItems.reduce((sum, row) => sum + row.lineTotal, 0);

  const availableMenu = useMemo(() => {
    const q = menuQuery.trim().toLowerCase();
    return menu.filter((m) => {
      if (m.isAvailable === false) return false;
      if (vegFilter === 'veg' && !m.isVeg) return false;
      if (vegFilter === 'nonveg' && m.isVeg) return false;
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q)
        || (m.description || '').toLowerCase().includes(q)
        || (m.categoryName || '').toLowerCase().includes(q)
      );
    });
  }, [menu, menuQuery, vegFilter]);

  const setQty = (menuItemId: string, quantity: number) => {
    const next = cart.filter((c) => c.menuItemId !== menuItemId);
    if (quantity > 0) next.push({ menuItemId, quantity });
    onCartChange(next);
  };

  return (
    <Stack spacing={1.25} sx={{ minHeight: 0, flex: 1, overflow: 'visible' }}>
      <Box sx={{ pt: 0.5, overflow: 'visible' }}>
      <Autocomplete
        fullWidth
        size="small"
        options={restaurants}
        value={selected}
        onChange={(_, v) => {
          onRestaurantChange(v?._id || '');
          onCartChange([]);
          setMenuQuery('');
        }}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a._id === b._id}
        filterOptions={(opts, state) => {
          const q = state.inputValue.trim().toLowerCase();
          if (!q) return opts;
          return opts.filter((r) =>
            r.name.toLowerCase().includes(q)
            || (r.cuisineTypes || []).some((c) => c.toLowerCase().includes(q))
            || (r.phone || '').includes(q),
          );
        }}
        renderOption={(props, option) => {
          const logo = resolveAssetUrl(option.logo || option.coverImage || '');
          return (
            <Box component="li" {...props} key={option._id} sx={{ display: 'flex !important', gap: 1.25, alignItems: 'center' }}>
              <Avatar
                src={logo || undefined}
                variant="rounded"
                sx={{ width: 40, height: 40, bgcolor: alpha(accent, 0.12), fontSize: 14, fontWeight: 800 }}
              >
                {(option.name || '?').slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={700} noWrap>{option.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {[
                    option.isOpen === false ? 'Closed' : 'Open',
                    (option.cuisineTypes || []).slice(0, 2).join(' · '),
                    option.rating != null ? `★ ${option.rating}` : '',
                    option.minOrderAmount != null ? `Min ${formatMoney(option.minOrderAmount)}` : '',
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label="Restaurant"
            placeholder="Search restaurant, cuisine…"
            error={!!fieldErrors.restaurantId}
            helperText={fieldErrors.restaurantId}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: true,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Avatar
                    src={resolveAssetUrl(selected?.logo || selected?.coverImage || '') || undefined}
                    variant="rounded"
                    sx={{
                      width: 28,
                      height: 28,
                      mr: 1,
                      flexShrink: 0,
                      bgcolor: alpha(accent, 0.12),
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {(selected?.name || 'R').slice(0, 1).toUpperCase()}
                  </Avatar>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                alignItems: 'center',
                minHeight: 40,
              },
              '& .MuiInputBase-input': {
                py: '8.5px',
              },
            }}
          />
        )}
      />
      </Box>

      {selected && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {selected.isOpen === false && <Chip size="small" label="Closed" color="warning" />}
          {selected.rating != null && <Chip size="small" label={`★ ${selected.rating}`} variant="outlined" />}
          {selected.avgPreparationTime != null && (
            <Chip size="small" label={`${selected.avgPreparationTime} min prep`} variant="outlined" />
          )}
          {selected.minOrderAmount != null && (
            <Chip size="small" label={`Min order ${formatMoney(selected.minOrderAmount)}`} variant="outlined" />
          )}
          {(selected.cuisineTypes || []).slice(0, 4).map((c) => (
            <Chip key={c} size="small" label={c} sx={{ bgcolor: alpha(accent, 0.08) }} />
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search menu items…"
          value={menuQuery}
          onChange={(e) => setMenuQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={0.5} flexShrink={0}>
          {([
            ['all', 'All'],
            ['veg', 'Veg'],
            ['nonveg', 'Non-veg'],
          ] as const).map(([key, label]) => (
            <Chip
              key={key}
              size="small"
              label={label}
              clickable
              onClick={() => setVegFilter(key)}
              color={vegFilter === key ? 'primary' : 'default'}
              variant={vegFilter === key ? 'filled' : 'outlined'}
              sx={vegFilter === key ? { bgcolor: accent } : undefined}
            />
          ))}
        </Stack>
      </Stack>

      {fieldErrors.cart && (
        <Typography variant="caption" color="error" fontWeight={700}>{fieldErrors.cart}</Typography>
      )}

      <Box sx={{
        flex: 1,
        minHeight: 140,
        maxHeight: 260,
        overflow: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        bgcolor: '#f8fafc',
        p: 1,
      }}>
        {availableMenu.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4, color: 'text.secondary' }}>
            <RestaurantMenu sx={{ mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">No menu items match</Typography>
          </Stack>
        ) : (
          <Grid container spacing={1}>
            {availableMenu.map((item) => {
              const qty = qtyById.get(item._id) || 0;
              const img = resolveAssetUrl(item.image || '');
              const price = unitPrice(item);
              return (
                <Grid item xs={12} sm={6} key={item._id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      display: 'flex',
                      gap: 1,
                      borderRadius: '10px',
                      border: `1px solid ${qty > 0 ? alpha(accent, 0.45) : '#e2e8f0'}`,
                      bgcolor: qty > 0 ? alpha(accent, 0.06) : '#fff',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '8px',
                        flexShrink: 0,
                        bgcolor: '#e2e8f0',
                        backgroundImage: img ? `url(${img})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {!img ? 'FOOD' : null}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '2px', flexShrink: 0,
                          bgcolor: item.isVeg ? '#16a34a' : '#dc2626',
                        }} />
                        <Typography variant="body2" fontWeight={700} noWrap title={item.name}>
                          {item.name}
                        </Typography>
                      </Stack>
                      {item.categoryName && (
                        <Typography variant="caption" color="text.secondary">{item.categoryName}</Typography>
                      )}
                      {item.description && (
                        <Typography variant="caption" color="text.secondary" sx={{
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {item.description}
                        </Typography>
                      )}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.5}>
                        <Typography variant="body2" fontWeight={800}>{formatMoney(price)}</Typography>
                        {qty === 0 ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setQty(item._id, 1)}
                            sx={{
                              minWidth: 0, px: 1.25, py: 0.15, borderRadius: '8px',
                              borderColor: accent, color: accent, fontWeight: 800, fontSize: 12,
                            }}
                          >
                            ADD
                          </Button>
                        ) : (
                          <Stack direction="row" alignItems="center" spacing={0.25} sx={{
                            border: `1px solid ${alpha(accent, 0.35)}`,
                            borderRadius: '8px',
                            bgcolor: '#fff',
                          }}>
                            <IconButton size="small" onClick={() => setQty(item._id, qty - 1)} sx={{ color: accent }}>
                              <Remove fontSize="small" />
                            </IconButton>
                            <Typography variant="caption" fontWeight={800} sx={{ minWidth: 16, textAlign: 'center' }}>
                              {qty}
                            </Typography>
                            <IconButton size="small" onClick={() => setQty(item._id, qty + 1)} sx={{ color: accent }}>
                              <Add fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {cartItems.length > 0 && (
        <Paper elevation={0} sx={{
          p: 1.25,
          borderRadius: '12px',
          border: `1px solid ${alpha(accent, 0.25)}`,
          bgcolor: alpha(accent, 0.04),
        }}>
          <Typography variant="caption" fontWeight={800} color="text.secondary" letterSpacing="0.06em">
            ORDER CART · {cartItems.reduce((n, r) => n + r.quantity, 0)} items
          </Typography>
          <Stack spacing={0.5} mt={0.75}>
            {cartItems.map((row) => (
              <Stack key={row.menuItemId} direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {row.item.name} × {row.quantity}
                </Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoney(row.lineTotal)}</Typography>
              </Stack>
            ))}
            <Stack direction="row" justifyContent="space-between" pt={0.5} sx={{ borderTop: '1px dashed #cbd5e1' }}>
              <Typography variant="body2" fontWeight={800}>Subtotal</Typography>
              <Typography variant="body2" fontWeight={800} color={accent}>{formatMoney(cartTotal)}</Typography>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
